import { AuthService, LoggerService, SharedUserService, TokenInitializationService } from '@agroone-front/shared'
import { environment } from '@front-app-environments/environment'
import { Store } from '@ngrx/store'
import { forkJoin, switchMap } from 'rxjs'
import { AppService } from './app.service'
import { loadSpecifications } from './scene/crop-management/crop/store/actions/specification.actions'
import { loadVarieties } from './scene/crop-management/crop/store/actions/variety.actions'
import { VarietyState } from './scene/crop-management/crop/store/reducers'
import { SpecificationState } from './scene/crop-management/crop/store/reducers/specification.reducer'
import { loadFarms } from './scene/crop-management/farm/store/actions/farm.actions'
import { FarmState } from './scene/crop-management/farm/store/reducers'
import { ConstantService } from './shared/constant/constant.service'
import { CropService } from './shared/crop/services/crop.service'
import { LanguageService } from './shared/languages/languages.service'
import { RegionService } from './shared/region/services/region.service'
import { SettingsService } from './shared/settings/settings.service'

export const init =
  (
    userService: SharedUserService,
    constantService: ConstantService,
    settingsService: SettingsService,
    regionService: RegionService,
    languageService: LanguageService,
    farmStore: Store<FarmState>,
    cropService: CropService,
    logger: LoggerService,
    authService: AuthService,
    varietyStore: Store<VarietyState>,
    specificationStore: Store<SpecificationState>,
    tokenInitializationService: TokenInitializationService,
    appService: AppService,
  ) =>
  async () => {
    console.log('init')
    loadExternalScript(environment.dynatraceScriptSrc)
    console.log('loadExternalScript')
    logger.init(environment.logLevel, environment.serverLogLevel)
    console.log('logger.init')
    authService.init(environment.cognitoDomain, environment.cognitoClientId, environment.apiUrl)
    console.log('authService.init')
    // Skip app init when logout
    if (document.location.pathname === '/logout') {
      return Promise.resolve()
    }

    console.log('tokenInitializationService.initializeToken')
    // Initialize standalone token first
    tokenInitializationService.initializeToken()
    console.log('tokenInitializationService.initializeToken')
    // The standalone token is the only way to know if the application is limited.
    // So if there is no standalone token we can safely assume the application is not limited.
    if (!tokenInitializationService.hasToken()) {
      appService.setIsLimitedApplication(false)
    }
    console.log('userService.init')

    return new Promise((resolve, _) => {
      // users/me and the regions list are independent, so load them in parallel
      // instead of the previous two-wave serialization (~900ms saved).
      forkJoin({
        user: userService.init(),
        regions: regionService.init(),
      })
        .pipe(
          // Both the user and the regions list are available now, so the active region
          // can be resolved before crops/specifications rely on it.
          switchMap(() => regionService.activeRegion),
          switchMap(() => {
            farmStore.dispatch(loadFarms())
            cropService.dispatchLoadCrops()
            // Varieties and specifications are only consumed inside the lazy-loaded
            // crop-management module, so they must not gate app bootstrap: dispatch
            // them fire-and-forget and let their stores fill in the background.
            varietyStore.dispatch(loadVarieties())
            specificationStore.dispatch(loadSpecifications())

            // languageService.init() reads userService.currentUser to resolve
            // currentLanguageId, so it must run after the user is loaded. Constants
            // and settings are used app-wide, so keep the bootstrap gated on these.
            return forkJoin({
              language: languageService.init(),
              constant: constantService.init(),
              settings: settingsService.init(),
            })
          }),
        )
        .subscribe({
          next: () => {
            logger.log('App init success')
            resolve(true)
          },
          error: (error) => {
            logger.error(error)
            resolve(false)
          },
        })
    })
  }

function loadExternalScript(scriptUrl: string): void {
  if (!scriptUrl || !navigator.onLine) {
    return
  }

  const scriptElement: HTMLScriptElement = document.createElement('script')
  scriptElement.src = scriptUrl
  scriptElement.type = 'text/javascript'
  document.body.appendChild(scriptElement)
}
